import { useEffect, useRef, useState } from 'react'
import { NavLink } from 'react-router-dom'
import { Bell, CheckCheck, CreditCard, AlertCircle, BanknoteIcon, Globe, EyeOff, FileText, FileX } from 'lucide-react'
import { useNotifications } from '@/hooks/useNotifications'
import type { AppNotification } from '@/hooks/useNotifications'

const TYPE_ICONS: Record<AppNotification['type'], React.ElementType> = {
  payment_success:       CreditCard,
  payment_failed:        AlertCircle,
  subscription_canceled: BanknoteIcon,
  subscription_updated:  CreditCard,
  site_published:        Globe,
  site_unpublished:      EyeOff,
  page_published:        FileText,
  page_unpublished:      FileX,
}

const TYPE_COLORS: Record<AppNotification['type'], string> = {
  payment_success:       'text-green-600 bg-green-50',
  payment_failed:        'text-red-600 bg-red-50',
  subscription_canceled: 'text-amber-600 bg-amber-50',
  subscription_updated:  'text-violet-600 bg-violet-50',
  site_published:        'text-green-600 bg-green-50',
  site_unpublished:      'text-gray-600 bg-gray-100',
  page_published:        'text-blue-600 bg-blue-50',
  page_unpublished:      'text-gray-600 bg-gray-100',
}

function fmtRelative(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime()
  const m = Math.floor(diff / 60000)
  if (m < 1) return 'Just now'
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ago`
  return `${Math.floor(h / 24)}d ago`
}

export function NotificationBell() {
  const { notifications, unreadCount, markRead, markAllRead } = useNotifications()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const recent = notifications.slice(0, 5)

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="relative flex items-center gap-3 w-full rounded-lg px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-colors"
      >
        <span className="relative">
          <Bell className="h-4 w-4" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-violet-600 text-[9px] font-bold text-white leading-none">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </span>
        Notifications
      </button>

      {open && (
        <div className="absolute bottom-full left-0 mb-1 w-80 rounded-xl border border-gray-200 bg-white shadow-lg z-50">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <span className="text-sm font-semibold text-gray-900">Notifications</span>
            {unreadCount > 0 && (
              <button
                onClick={() => { void markAllRead() }}
                className="flex items-center gap-1 text-xs text-violet-600 hover:text-violet-800"
              >
                <CheckCheck className="h-3 w-3" />
                Mark all read
              </button>
            )}
          </div>

          {recent.length === 0 ? (
            <p className="py-6 text-center text-sm text-gray-400">No notifications</p>
          ) : (
            <ul>
              {recent.map((n) => {
                const Icon = TYPE_ICONS[n.type]
                const color = TYPE_COLORS[n.type]
                return (
                  <li key={n.id} className="border-b border-gray-50">
                    <button
                      type="button"
                      onClick={() => { if (!n.read) void markRead(n.id) }}
                      className={[
                        'flex w-full gap-3 px-4 py-3 text-left hover:bg-gray-50',
                        !n.read && 'bg-violet-50/30',
                      ].filter(Boolean).join(' ')}
                    >
                      <span className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${color}`}>
                        <Icon className="h-3.5 w-3.5" />
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm ${n.read ? 'text-gray-700' : 'text-gray-900 font-medium'}`}>{n.title}</p>
                        {n.body && <p className="text-xs text-gray-400 mt-0.5 truncate">{n.body}</p>}
                        <p className="text-[10px] text-gray-300 mt-0.5">{fmtRelative(n.created_at)}</p>
                      </div>
                      {!n.read && <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-violet-500" />}
                    </button>
                  </li>
                )
              })}
            </ul>
          )}

          <div className="px-4 py-2.5 border-t border-gray-100">
            <NavLink
              to="/notifications"
              onClick={() => setOpen(false)}
              className="block text-center text-xs text-violet-600 hover:text-violet-800 font-medium"
            >
              View all notifications →
            </NavLink>
          </div>
        </div>
      )}
    </div>
  )
}
